/**
 * Steel Plate Tool (BIM: Steel > Fabricate Elements > Plate)
 *
 * Sketch-driven placement: click points on a horizontal work plane (elevation
 * follows the `steelPlateElevation` tool property), finish with Enter or by
 * clicking near the first point to close the loop. Thickness comes from
 * `steelPlateThickness` (mm). Mirrors BIM's modeled-by-skin plate workflow.
 */

import { BaseTool } from '../BaseTool';
import { ThreeEvent } from '@react-three/fiber';
import * as THREE from 'three';
import { SteelFabricationManager } from '../../core/SteelFabricationManager';
import i18n from '../../i18n';

export class SteelPlateTool extends BaseTool {
    id = 'steel_plate';
    name = 'Steel Plate';
    cursor = 'crosshair';

    private sketchPoints: THREE.Vector3[] = [];

    private get plateElevation(): number {
        return this.context?.bimStore.toolProperties.steelPlateElevation ?? 0;
    }

    private get plateThicknessM(): number {
        const mm = this.context?.bimStore.toolProperties.steelPlateThickness ?? 10;
        return Math.max(mm, 1) / 1000;
    }

    onPointerDown(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        if (event.button !== 0) return;

        const point = this.getIntersection(event, this.plateElevation);
        if (!point) return;
        const snapped = this.snapToGrid(new THREE.Vector3(point.x, point.y, this.plateElevation));

        // Close the loop when clicking near the first sketch point.
        if (this.sketchPoints.length >= 3) {
            const first = this.sketchPoints[0];
            if (first.distanceTo(snapped) < 0.25) {
                this.finishPlate();
                return;
            }
        }

        this.sketchPoints.push(snapped);
        this.updatePreview();
    }

    onPointerMove(event: ThreeEvent<PointerEvent>): void {
        if (!this.context || this.sketchPoints.length === 0) return;

        const point = this.getIntersection(event, this.plateElevation);
        if (!point) return;
        const snapped = this.snapToGrid(new THREE.Vector3(point.x, point.y, this.plateElevation));
        this.updatePreview(snapped);
    }

    onPointerUp(_event: ThreeEvent<PointerEvent>): void { }

    onKeyDown(event: KeyboardEvent): void {
        if (event.key === 'Enter') {
            this.finishPlate();
            event.preventDefault();
            return;
        }
        if (event.key === 'Backspace' && this.sketchPoints.length > 0) {
            this.sketchPoints.pop();
            this.updatePreview();
            event.preventDefault();
            return;
        }
        super.onKeyDown(event);
    }

    private finishPlate(): void {
        if (!this.context) return;
        if (this.sketchPoints.length < 3) {
            this.notify(i18n.t('tools.notify.plateSketchNeedsAtLeast3'), 'warning');
            return;
        }
        const plate = SteelFabricationManager.createPlate(
            this.sketchPoints,
            this.plateThicknessM,
            { materialName: 'Steel Plate', name: `Steel Plate ${this.context.bimStore.project.nextElementId}` }
        );
        if (plate) {
            this.context.bimStore.addElement(plate);
            this.notify(i18n.t('tools.notify.plateCreated', { thickness: (this.plateThicknessM * 1000).toFixed(0) }), 'success');
        }
        this.sketchPoints = [];
        this.clearPreview();
    }

    private updatePreview(cursor?: THREE.Vector3): void {
        if (!this.context) return;
        if (this.sketchPoints.length === 0) {
            this.context.appStore.setToolPreview(null);
            return;
        }
        const points = cursor ? [...this.sketchPoints, cursor] : [...this.sketchPoints];
        this.context.appStore.setToolPreview({
            type: 'polygon',
            points,
            color: '#8aa0b8',
        });
    }
}
